import { NavLink, Outlet, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/authStore';
import AdminAlertBell from '@/components/AdminAlertBell';
import {
  ChartBarIcon,
  UsersIcon,
  LifebuoyIcon,
  BellAlertIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FireIcon,
  CreditCardIcon,
  CurrencyEuroIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const ADMIN_NAV: Array<{
  path: string;
  end?: boolean;
  icon: typeof ChartBarIcon;
  label: string;
}> = [
  { path: '/admin', end: true, icon: ChartBarIcon, label: 'admin.nav.overview' },
  { path: '/admin/users', icon: UsersIcon, label: 'admin.nav.users' },
  { path: '/admin/tickets', icon: LifebuoyIcon, label: 'admin.nav.tickets' },
  { path: '/admin/alerts', icon: BellAlertIcon, label: 'admin.nav.alerts' },
  { path: '/admin/activity', icon: ClockIcon, label: 'admin.nav.activity' },
  { path: '/admin/errors', icon: ExclamationTriangleIcon, label: 'admin.nav.errors' },
  { path: '/admin/hot-leads', icon: FireIcon, label: 'admin.nav.hotLeads' },
  { path: '/admin/subscriptions', icon: CreditCardIcon, label: 'admin.nav.subscriptions' },
  { path: '/admin/plan-pricing', icon: CurrencyEuroIcon, label: 'admin.nav.planPricing' },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [mobileOpen, setMobileOpen] = useState(false);

  const sidebarContent = (
    <div className="flex h-full flex-col bg-slate-900 text-slate-100">
      <div className="flex h-16 items-center gap-2 border-b border-slate-800 px-5">
        <ShieldCheckIcon className="h-6 w-6 text-amber-400" />
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t('admin.title')}
          </div>
          <div className="text-sm font-semibold">{t('admin.console')}</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {ADMIN_NAV.map(({ path, end, icon: Icon, label }) => (
          <NavLink
            key={path}
            to={path}
            end={end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white',
              )
            }
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span>{t(label)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <div className="mb-3 rounded-lg bg-slate-800/60 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {t('admin.signedInAs')}
          </div>
          <div className="truncate text-sm font-medium text-white">{user?.email}</div>
          <div className="mt-1 inline-flex rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-300">
            {t('admin.superAdmin')}
          </div>
        </div>
        <Link
          to="/"
          className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700"
        >
          <ArrowLeftOnRectangleIcon className="h-4 w-4" />
          {t('admin.exitAdmin')}
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 shadow-2xl">{sidebarContent}</aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 md:block">{sidebarContent}</aside>

      <div className="md:ml-64">
        {/* Top bar — visible on all screens, bell on the right */}
        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:px-8">
          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100 md:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            {mobileOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
          </button>
          <span className="text-sm font-semibold text-slate-900">{t('admin.console')}</span>
          <div className="ml-auto flex items-center gap-2">
            <AdminAlertBell />
          </div>
        </div>

        <main className="px-4 py-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
